import React, { useMemo, useState } from 'react';
import { ui } from './toolTheme';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const FORMATS = [
  'png', 'jpg', 'jpeg', 'webp', 'bmp', 'gif', 'tiff', 'ico',
];

const ImageConverterPage = ({ setPage }) => {
  const [file, setFile] = useState(null);
  const [targetFormat, setTargetFormat] = useState('png');
  const [quality, setQuality] = useState(92);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileInfo = useMemo(() => {
    if (!file) return null;
    return `${file.name} • ${(file.size / 1024).toFixed(1)} KB`;
  }, [file]);

  const handleConvert = async () => {
    setError('');
    setSuccess('');
    if (!file) {
      setError('Please choose an image file first.');
      return;
    }

    setConverting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target_format', targetFormat);
      fd.append('quality', String(quality));

      const res = await fetch(`${API_BASE}/api/image/convert`, {
        method: 'POST',
        body: fd,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || data.error || `Convert failed (${res.status})`);
      }

      const blob = await res.blob();
      const ext = targetFormat === 'jpeg' ? 'jpg' : targetFormat;
      const base = file.name.replace(/\.[^.]+$/, '');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${base}_converted.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess('Converted and downloaded successfully.');
    } catch (e) {
      setError(e.message || 'Conversion failed.');
    } finally {
      setConverting(false);
    }
  };

  return (
    <main style={ui.page}>
      <div style={{ ...ui.container, maxWidth: 860 }}>
        <div style={{ marginBottom: 26 }}>
          <button onClick={() => setPage('/tools')} style={ui.backBtn}>
            ← Back to Tools
          </button>
        </div>

        <div style={ui.card}>
          <h1 style={ui.sectionTitle}>
            Image Type Converter
          </h1>
          <p style={ui.sectionSub}>
            Convert image formats instantly (JPG/JPEG ↔ PNG, WEBP, BMP, GIF, TIFF, ICO).
          </p>
        </div>

        <div style={{ ...ui.card, marginTop: 18 }}>
          <label style={ui.label}>
            Select Image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            style={{ ...ui.fileInput, marginBottom: 10 }}
          />
          {fileInfo && <div style={{ color: '#94a3b8', fontSize: 12 }}>{fileInfo}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
            <div>
              <label style={ui.label}>
                Target Format
              </label>
              <select
                value={targetFormat}
                onChange={(e) => setTargetFormat(e.target.value)}
                style={ui.select}
              >
                {FORMATS.map((fmt) => (
                  <option key={fmt} value={fmt}>{fmt.toUpperCase()}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={ui.label}>
                Quality (JPEG/WEBP)
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={quality}
                onChange={(e) => setQuality(Math.max(1, Math.min(100, Number(e.target.value) || 92)))}
                style={ui.input}
              />
            </div>
          </div>

          <button
            onClick={handleConvert}
            disabled={converting}
            style={{ ...ui.primaryBtn, marginTop: 16, opacity: converting ? 0.65 : 1, cursor: converting ? 'not-allowed' : 'pointer' }}
          >
            {converting ? 'Converting…' : 'Convert & Download'}
          </button>

          {error && <div style={{ ...ui.error, marginTop: 12 }}>{error}</div>}
          {success && <div style={{ ...ui.success, marginTop: 12 }}>{success}</div>}
        </div>
      </div>
    </main>
  );
};

export default ImageConverterPage;
